import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useMotionValue } from 'framer-motion';

import Card from './Card/Card';
import CardPlaceholder from './Card/CardPlaceholder';
import MatchedPopup from './MatchedPopup/MatchedPopup';
import SendGiftPopup from './SendGiftPopup/SendGiftPopup';

import Modal from '@/animations/Modal.tsx';
import { CandidateUI } from '@/api/matchmaker/types.ts';
import UIConfig from '@/UIConfig.ts';
import PaywallPopup from '@/components/Popup/PaywallPopup/PaywallPopup.tsx';
import { UserInit } from '@/api/login/types.ts';
import { PaywallTypes, ProductInvoicePlacements } from '@/enums/apiEnums.ts';
import usePolicy from '@/hooks/usePolicy.ts';
import NoResults from '@/components/Discovery/Card/placeholders/NoResults.tsx';
import { ProfileHiddenLock } from '@/components/Discovery/Card/placeholders/ProfileHiddenLock.tsx';
import { InsufficientData } from '@/components/Discovery/Card/placeholders/InsufficientData.tsx';
import { GeolocationModal } from '@/components/Discovery/GeolocationModal.tsx';
import { useGeolocationControl } from '@/hooks/useGeolocationControl.tsx';
import { useSummariesStore } from '@/store/useSummariesStore.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import {
  customCardsConfig,
  isCustomCard
} from '@/components/Discovery/Card/CustomCardsConfig.ts';
import MatchMakerService from '@/api/matchmaker/matchmaker.ts';
import { PendingVerification } from '@/components/Discovery/Card/placeholders/PendingVerification.tsx';
import { CompleteProfile } from '@/components/Discovery/Card/placeholders/CompleteProfile.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { helpers } from '@/helpers.ts';
import { MessageTypesUI } from '@/enums/viewEnums.ts';
import { LikesLimitToast } from '@/components/Toast/LikesLimitToast.tsx';
import CenteredModal from '@/animations/CenteredModal.tsx';
import LikesLimitPopup from '@/components/Discovery/LikesLimitPopup/LikesLimitPopup.tsx';
import { useDiscoveryStore } from '@/store/useDiscoveryStore.ts';
import { LIKES_LIMIT_REACHED } from '@/constants/error_codes.ts';
import CardSkeleton from '@/components/Discovery/Card/CardSkeleton.tsx';
import PremiumUpsalePopup from '@/components/Popup/PremiumUpsalePopup/PremiumUpsalePopup.tsx';
import { usePremiumUpsaleDiscoveryPopupControl } from '@/hooks/usePremiumUpsaleDiscoveryPopupControl.tsx';
import { useJoyrideTourStore } from '@/store/useJoyrideTourStore.ts';
import { useCloudStorage } from '@/hooks/useStorage.ts';
import { discoveryTourSteps } from '@/constants/tourSteps.tsx';
import { TOUR_KEYS } from '@/constants/local_storage_keys.ts';
import { ReactionsUpsalePopup } from '@/components/Popup/ReactionsUpsalePopup/ReactionsUpsalePopup.tsx';
import { useReactionsUpsale } from '@/hooks/useReactionsUpsale.ts';

export enum SwipeType {
  SLOW = 'slow',
  FAST = 'fast'
}

export interface SwipeRef {
  swipeRight: (params: {
    type?: SwipeType;
    ignoreLikeRequest?: boolean;
  }) => void;
  swipeLeft: (type: SwipeType) => void;
}

interface DiscoveryProps {
  aboutMe: UserInit | null;
  remainingAmount: number;
}

const Discovery = ({ remainingAmount, aboutMe }: DiscoveryProps) => {
  const {
    isLoading,
    isEmpty,
    fetchCandidates,
    removeCandidate,
    candidates,
    isExpired,
    isMatchPopupOpen,
    openMatchPopup
  } = useDiscoveryStore();

  const { isInsufficientData, isHidden, isIncognitoMode, isBeingVerified } =
    usePolicy();

  const [isLikesLimitPopupOpen, setIsLikesLimitPopupOpen] =
    useState<boolean>(false);

  const closeLikesLimitPopup = () => setIsLikesLimitPopupOpen(false);

  const { isSkipCompleteProfilePlaceholder, isProfileFullyCompleted } =
    useProfileStore();

  const { getLikesCount } = useSummariesStore();

  const [premiumPaywallOpen, setPremiumPaywallOpen] = useState<boolean>(false);

  const { geoModalOpen, closeGeoModal } = useGeolocationControl();

  const { premiumUpsaleOpen, closePremiumUpsale } =
    usePremiumUpsaleDiscoveryPopupControl();

  const storage = useCloudStorage();
  const { startTour } = useJoyrideTourStore();

  const {
    reactionsUpsaleOpen,
    likedCandidate,
    openReactionsUpsale,
    closeReactionsUpsale
  } = useReactionsUpsale(remainingAmount);

  const openedFromReactionsUpsaleRef = useRef(false);

  const closePremiumPaywall = () => {
    setPremiumPaywallOpen(false);
    openedFromReactionsUpsaleRef.current = false;
  };

  const openPremiumPaywall = () => {
    openedFromReactionsUpsaleRef.current = false;
    setPremiumPaywallOpen(true);
  };

  const openPremiumPaywallFromReactionsUpsale = () => {
    openedFromReactionsUpsaleRef.current = true;
    setPremiumPaywallOpen(true);
  };

  const handleOpenSendReactionFromUpsale = () => {
    if (likedCandidate) {
      setGiftedCandidate(likedCandidate);
    }
  };

  const afterPremiumPaid = async () => {
    if (openedFromReactionsUpsaleRef.current) {
      handleOpenSendReactionFromUpsale();
    }
    closePremiumPaywall();
    return Promise.resolve();
  };

  const cardRefs = useRef<(SwipeRef | null)[]>([]);

  // Shared MotionValue for front card X position - used by back cards for scale animation
  const sharedFrontCardX = useMotionValue(0);

  const [matchedCandidate, setMatchedCandidate] = useState<CandidateUI | null>(
    null
  );
  const [giftedCandidate, setGiftedCandidate] = useState<CandidateUI | null>(
    null
  );

  const closeNewMatch = () => {
    setMatchedCandidate(null);
    openMatchPopup(false);
  };

  const openSendGiftPopup = (candidate: CandidateUI) => {
    setGiftedCandidate(candidate);
  };

  const closeSendGift = (swipeRight?: boolean) => {
    setGiftedCandidate(null);
    if (swipeRight) {
      setTimeout(() => handleSwipeRightOnFrontCard(), 200);
    }
  };

  const handleLike = async (candidate: CandidateUI, nextCardHid?: string) => {
    const {
      likesMe,
      profile: { hid }
    } = candidate;

    try {
      const likesQuota = await MatchMakerService.likeMatchCandidate(hid);

      // Show toast only if likesQuota is not null (non-premium users) and not likes me
      if (likesQuota && !likesMe) {
        helpers.toast({
          id: MessageTypesUI.TYPE_LIKES_LIMIT,
          content: (
            <LikesLimitToast
              totalLikes={likesQuota.limit}
              likesLeft={likesQuota.remaining}
            />
          )
        });
      }

      const isUnlimitedLikes = likesQuota === null;
      const likesLimitNotReached =
        isUnlimitedLikes || likesQuota?.remaining !== -1;

      const isNextCardCustom = nextCardHid ? isCustomCard(nextCardHid) : false;

      if (
        likesLimitNotReached &&
        isUnlimitedLikes !== null &&
        !isNextCardCustom
      ) {
        openReactionsUpsale(candidate);
      }

      if (isUnlimitedLikes || likesLimitNotReached) {
        await removeCandidate(hid);
      }

      if (likesMe && likesLimitNotReached) {
        void getLikesCount();
        setMatchedCandidate(candidate);
        openMatchPopup(true);
      }

      return likesQuota;
    } catch (e) {
      // @ts-expect-error because we receive custom errors from BE
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (e.response?.data?.code === LIKES_LIMIT_REACHED) {
        setIsLikesLimitPopupOpen(true);
        return { remaining: -1, limit: 0 };
      } else {
        catchErrors(e, true);
        return null;
      }
    }
  };

  const handlePass = async (candidate: CandidateUI) => {
    const {
      profile: { hid }
    } = candidate;
    try {
      await MatchMakerService.passMatchCandidate(hid);
    } catch (e) {
      catchErrors(e, true);
    } finally {
      await removeCandidate(hid);
    }
  };

  // Call swipeRight on the front card (last in subCandidates)
  const handleSwipeRightOnFrontCard = () => {
    if (subCandidates.length > 0) {
      const frontCardRef = cardRefs.current[subCandidates.length - 1] as
        | SwipeRef
        | undefined;
      if (frontCardRef) {
        frontCardRef.swipeRight({
          type: SwipeType.SLOW,
          ignoreLikeRequest: true
        });
      }
    }
  };

  // Layout only 2 cards at once
  const subCandidates = candidates.slice(-3);
  const isShowGiftPopup = giftedCandidate !== null;

  // Reset sharedX when candidates change (card was removed)
  useEffect(() => {
    sharedFrontCardX.set(0);
  }, [candidates.length]);

  useEffect(bodyScrollControl, []);

  useEffect(() => {
    if (
      !isInsufficientData &&
      !isHidden &&
      !isBeingVerified &&
      (candidates.length === 0 || isExpired())
    ) {
      fetchCandidates().catch((e) => {
        catchErrors(e, true);
      });
    }
  }, [candidates.length, isInsufficientData, isBeingVerified, isIncognitoMode]);

  useEffect(() => {
    const hasCards = subCandidates.length > 0;
    const noPopupsOpen =
      !isMatchPopupOpen &&
      !isShowGiftPopup &&
      !premiumPaywallOpen &&
      !geoModalOpen &&
      !premiumUpsaleOpen &&
      !isLikesLimitPopupOpen;

    const noCardPlaceholders =
      !isBeingVerified && !isInsufficientData && !isHidden && !isEmpty;

    const frontCard = subCandidates[subCandidates.length - 1];
    const isFrontCardCustom = frontCard
      ? isCustomCard(frontCard.profile.hid)
      : false;

    if (
      hasCards &&
      !isLoading &&
      noPopupsOpen &&
      noCardPlaceholders &&
      !isFrontCardCustom
    ) {
      const timer = setTimeout(() => {
        void startTour(TOUR_KEYS.DISCOVERY, discoveryTourSteps, storage);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [
    subCandidates.length,
    isLoading,
    isMatchPopupOpen,
    isShowGiftPopup,
    premiumPaywallOpen,
    geoModalOpen,
    premiumUpsaleOpen,
    isLikesLimitPopupOpen,
    isInsufficientData,
    isHidden,
    isEmpty,
    isBeingVerified
  ]);

  const getCardPlaceholder = useCallback(() => {
    if (
      isBeingVerified &&
      !isSkipCompleteProfilePlaceholder &&
      !isProfileFullyCompleted()
    )
      return <CompleteProfile />;
    if (isBeingVerified) return <PendingVerification />;
    if (isInsufficientData) return <InsufficientData />;
    if (isHidden) return <ProfileHiddenLock />;
    if (isEmpty) return <NoResults />;
  }, [
    isEmpty,
    isInsufficientData,
    isHidden,
    isBeingVerified,
    isSkipCompleteProfilePlaceholder,
    isProfileFullyCompleted()
  ]);

  return (
    <div style={containerStyle}>
      <Modal open={reactionsUpsaleOpen} handleClose={closeReactionsUpsale}>
        <ReactionsUpsalePopup
          onClose={closeReactionsUpsale}
          candidate={likedCandidate}
          onOpenPremiumPaywall={openPremiumPaywallFromReactionsUpsale}
          onOpenSendReaction={handleOpenSendReactionFromUpsale}
        />
      </Modal>

      <Modal
        fullScreen
        open={isMatchPopupOpen && matchedCandidate !== null}
        handleClose={closeNewMatch}
      >
        <MatchedPopup
          handleClose={closeNewMatch}
          candidate={matchedCandidate}
          avatar={aboutMe?.profile.v2photos[0]}
        />
      </Modal>

      <Modal fullScreen open={isShowGiftPopup} handleClose={closeSendGift}>
        <SendGiftPopup
          giftedContactId={giftedCandidate?.profile.hid ?? ''}
          giftedContactName={giftedCandidate?.profile.name ?? ''}
          giftedContactPhoto={giftedCandidate?.profile.v2photos?.[0]}
          handleClose={closeSendGift}
          remainingAmount={remainingAmount}
          haptic={false}
          placement={ProductInvoicePlacements.MATCHMAKER_SEND_REACTION_BUTTON}
          hideBackButton={true}
        />
      </Modal>

      <Modal
        fullScreen
        scrollable
        open={premiumPaywallOpen}
        handleClose={closePremiumPaywall}
      >
        <PaywallPopup
          handleClose={closePremiumPaywall}
          type={PaywallTypes.TYPE_SUBSCRIPTION}
          placement={
            openedFromReactionsUpsaleRef.current
              ? ProductInvoicePlacements.MATCHMAKER_REACTIONS_AND_PREMIUM_UPSALE_POPUP
              : ProductInvoicePlacements.MATCHMAKER_LIKES_LIMIT_POPUP
          }
          callAfterPaywallAction={afterPremiumPaid}
          hideBackButton={true}
        />
      </Modal>

      <Modal
        open={geoModalOpen && !premiumUpsaleOpen}
        handleClose={closeGeoModal}
      >
        <GeolocationModal handleClose={closeGeoModal} />
      </Modal>

      <Modal
        fullScreen
        open={premiumUpsaleOpen}
        handleClose={closePremiumUpsale}
      >
        <PremiumUpsalePopup handleClose={closePremiumUpsale} />
      </Modal>

      <CenteredModal
        open={isLikesLimitPopupOpen}
        handleClose={closeLikesLimitPopup}
      >
        <LikesLimitPopup
          openPaywall={openPremiumPaywall}
          handleClose={closeLikesLimitPopup}
        />
      </CenteredModal>

      {isLoading && <CardSkeleton />}

      {!isLoading &&
        subCandidates.map((candidate, i) => {
          const hid = candidate.profile.hid;
          if (isCustomCard(hid)) {
            const config = customCardsConfig.find((c) => c.hid === hid);
            if (config) {
              const CustomCardComponent = config.component;
              const isCustomFront = subCandidates.length - 1 === i;
              return (
                <CustomCardComponent
                  ref={(el: SwipeRef | null) => (cardRefs.current[i] = el)}
                  isFrontCard={isCustomFront}
                  key={hid}
                  sharedX={sharedFrontCardX}
                />
              );
            }
          }

          const isFront = subCandidates.length - 1 === i;

          const nextCardHid = subCandidates[i - 1]?.profile.hid;

          return (
            <Card
              ref={(el: SwipeRef | null) => (cardRefs.current[i] = el)}
              key={candidate.profile.hid}
              matchCandidate={candidate}
              isFrontCard={isFront}
              handleLike={handleLike}
              handlePass={handlePass}
              openSendGiftPopup={() => openSendGiftPopup(candidate)}
              sharedX={sharedFrontCardX}
              nextCardHid={nextCardHid}
            />
          );
        })}

      {(isEmpty || isInsufficientData || isHidden || isBeingVerified) && (
        <CardPlaceholder>{getCardPlaceholder()}</CardPlaceholder>
      )}
    </div>
  );
};

const bodyScrollControl = () => {
  document.body.style.overflow = 'hidden';
  const preventScroll = (e: TouchEvent) => {
    // Don't block scroll inside modals
    const target = e.target as HTMLElement;
    if (target.closest('.modal')) {
      return;
    }

    e.preventDefault();
  };
  window.addEventListener('touchmove', preventScroll, { passive: false });
  return () => {
    document.body.style.overflow = '';
    window.removeEventListener('touchmove', preventScroll);
  };
};

const containerStyle: CSSProperties = {
  width: '100vw',
  height: '100vh',
  overflow: 'hidden',
  background: UIConfig.Colors.BackgroundDark
};

export default Discovery;
